import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loader2, Check, XCircle, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

/**
 * Landing page after Stripe Embedded Checkout completes.
 * Stripe redirects the iframe to /billing/complete?session_id={CHECKOUT_SESSION_ID}
 * We poll GET /api/billing/checkout/status/{session_id} until payment_status='paid',
 * then redirect the user to the owner Dashboard.
 */
export default function BillingComplete() {
  const [search] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = search.get("session_id");
  const [state, setState] = useState("polling"); // polling | success | failed
  const [msg, setMsg] = useState("Confirmation du paiement en cours…");
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      setMsg("Session introuvable.");
      return;
    }
    let done = false;
    let n = 0;
    const poll = async () => {
      n += 1;
      setAttempts(n);
      try {
        const { data } = await api.get(`/billing/checkout/status/${sessionId}`);
        if (data.payment_status === "paid" || data.status === "complete") {
          done = true;
          clearInterval(timerRef.current);
          setState("success");
          setMsg("Paiement confirmé. Votre abonnement est activé.");
          try { await refresh(); } catch {}
          setTimeout(() => nav("/dashboard", { replace: true }), 2000);
          return;
        }
        if (data.status === "expired") {
          done = true;
          clearInterval(timerRef.current);
          setState("failed");
          setMsg("La session de paiement a expiré. Vous pouvez recommencer.");
          return;
        }
        setMsg(`En attente de la confirmation Stripe… (tentative ${n})`);
      } catch (e) {
        setMsg(`Erreur temporaire (${n}). Nouvel essai…`);
      }
      if (n >= 20 && !done) {
        clearInterval(timerRef.current);
        setState("failed");
        setMsg("Le paiement prend plus de temps que prévu. Rafraîchissez la page dans quelques instants.");
      }
    };
    poll();
    timerRef.current = setInterval(poll, 2500);
    return () => timerRef.current && clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div data-testid="billing-complete-page" className="min-h-screen text-[#F5F0EB]">
      <Header />
      <section className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <div className="overline mb-4">CAFÉ-SPOT · Abonnement</div>
          {state === "polling" && (
            <>
              <Loader2 className="mx-auto text-[#C5A059] animate-spin mb-6" size={48} />
              <h1 data-testid="billing-complete-title" className="font-serif text-4xl md:text-5xl font-light">Finalisation en cours</h1>
              <p data-testid="billing-complete-msg" className="mt-6 text-[#A8A39D]">{msg}</p>
              <p className="mt-2 text-xs tracking-widest uppercase text-[#5D4037]">Ne fermez pas cette page</p>
            </>
          )}
          {state === "success" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full border-2 border-[#C5A059] flex items-center justify-center">
                <Check className="text-[#C5A059]" size={40} strokeWidth={2} />
              </div>
              <h1 data-testid="billing-complete-title" className="font-serif text-4xl md:text-5xl font-light text-[#C5A059]">Paiement confirmé</h1>
              <p data-testid="billing-complete-msg" className="mt-6 text-[#F5F0EB] text-lg">{msg}</p>
              <p className="mt-4 text-sm text-[#A8A39D]">Redirection vers votre espace propriétaire…</p>
              <Link to="/dashboard" className="btn-gold mt-8 inline-flex" data-testid="go-to-dashboard">
                Accéder au Dashboard <ArrowRight size={14} />
              </Link>
            </>
          )}
          {state === "failed" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full border-2 border-orange-400 flex items-center justify-center">
                <XCircle className="text-orange-400" size={40} strokeWidth={2} />
              </div>
              <h1 data-testid="billing-complete-title" className="font-serif text-4xl md:text-5xl font-light text-orange-400">Problème de finalisation</h1>
              <p data-testid="billing-complete-msg" className="mt-6 text-[#F5F0EB]">{msg}</p>
              <div className="mt-8 flex gap-4 justify-center">
                <Link to="/dashboard/billing" className="btn-gold" data-testid="retry-checkout">Réessayer</Link>
                <Link to="/dashboard" className="btn-ghost">Retour au Dashboard</Link>
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
